import React, { useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useDealerApprovalInbox from '../hooks/useDealerApprovalInbox';
import { useLazyGetDealerApprovalByIdQuery } from '../../../../api/endpoints/dealerApproval';
import { resolveRoleText, resolveTargetTypeText } from '../../../../utils/dealerApprovalEnums';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { toastService } from '../../../../utils/toastService';

const DealerApprovalInboxPage = () => {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();
    const languageId = getCurrentLanguageId();
    const { items, totalCount, loading, pagination, onPageChange } = useDealerApprovalInbox();
    const [fetchApprovalById] = useLazyGetDealerApprovalByIdQuery();
    const [openingId, setOpeningId] = useState(null);

    // Inbox satırından birleşik ekrana git. dealerAddressBrandId satırda varsa
    // doğrudan kullan; yoksa GetById ile çözümle (backend alan adını taşımıyor olabilir).
    const openRecord = async (row) => {
        if (!row) return;
        if (row.sapDealerId) {
            navigate(`/management/dealer-registration/dealer/${row.sapDealerId}`, { state: { from: 'approval-inbox' } });
            return;
        }
        const requestId = row.requestId;
        let brandId = row.dealerAddressBrandId ?? row.DealerAddressBrandId;
        if (!brandId && requestId) {
            setOpeningId(requestId);
            try {
                const detail = await fetchApprovalById({ id: requestId, languageId }).unwrap();
                brandId = detail?.dealerAddressBrandId ?? detail?.DealerAddressBrandId;
            } catch {
                toastService.error(th('common.error'));
                setOpeningId(null);
                return;
            }
            setOpeningId(null);
        }
        if (!brandId) {
            toastService.error(th('common.error'));
            return;
        }
        navigate(`/management/dealer-registration/edit/${brandId}`, { state: { from: 'approval-inbox' } });
    };

    // Onay sırası: kimde beklediği (rol) + hangi adımda olduğu tek bakışta.
    const waitingBody = (row) => {
        const role = resolveRoleText(th, row.currentStepRole);
        const current = row.currentStepOrder ?? '-';
        const total = row.totalSteps ?? '-';
        const stepText = th('dealerApproval.stepSummary', { current, total });
        const label = role ? `${role} — ${stepText}` : stepText;
        return <Tag value={label} severity="warning" icon="pi pi-clock" />;
    };
    const newBody = (row) => <span>{row.isNewRegistration ? th('common.yes') : th('common.no')}</span>;
    const tabChangesBody = (row) => {
        const changes = row.tabChanges || [];
        if (!changes.length) return <span>-</span>;
        return (
            <div className="flex flex-wrap gap-1">
                {changes.map((c) => (
                    <Tag key={c.targetType} value={`${resolveTargetTypeText(th, c.targetType)}${c.totalCount > 0 ? ` (${c.totalCount})` : ''}`} severity="warning" />
                ))}
            </div>
        );
    };
    const actionBody = (row) => {
        const isOpening = openingId === row.requestId;
        return (
            <div className="flex align-items-center justify-content-center gap-3">
                <i
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!isOpening) openRecord(row);
                    }}
                    className={`pi ${isOpening ? 'pi-spin pi-spinner' : 'pi-eye'} cursor-pointer text-blue-500 hover:text-blue-700`}
                    style={{ fontSize: '1.1rem' }}
                    title={th('common.view')}
                />
            </div>
        );
    };

    return (
        <div className="card">
            <h4 className="m-0 mb-3">{th('dealerApproval.inboxTitle')}</h4>
            <DataTable
                value={items}
                loading={loading}
                lazy
                paginator
                rows={pagination.rows}
                first={pagination.first}
                totalRecords={totalCount}
                onPage={onPageChange}
                dataKey="requestId"
                emptyMessage={th('common.noRecords')}
                selectionMode="single"
                onRowClick={(e) => openRecord(e.data)}
                rowClassName={() => 'cursor-pointer'}
                showGridlines
                stripedRows
                tableStyle={{ minWidth: '50rem' }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate={th('table.paginatorTemplate')}
            >
                <Column body={actionBody} exportable={false} bodyClassName="text-center" headerClassName="text-center" style={{ width: '5%', minWidth: '53px' }} />
                <Column field="dealerName" header={th('dealerApproval.column.dealerName')} />
                <Column field="sapDealerId" header="SAP" />
                <Column header={th('dealerRegistration.column.addressName')} body={(row) => th('dealerRegistration.addressCount', { count: row.addressCount ?? 0 })} />
                <Column header={th('dealerApproval.column.waitingAt')} body={waitingBody} />
                <Column field="changeCount" header={th('dealerApproval.column.changeCount')} />
                <Column header={th('dealerApproval.tab.changedTabs')} body={tabChangesBody} style={{ minWidth: '180px' }} />
                <Column header={th('dealerApproval.isNewRegistration')} body={newBody} />
                <Column field="createdName" header={th('dealerApproval.column.createdName')} />
                <Column header={th('dealerApproval.column.createdDate')} body={(row) => dateOnlyBodyTemplate(row, 'createdDate')} />
            </DataTable>
        </div>
    );
};

export default DealerApprovalInboxPage;
